import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, Sparkles, TrendingUp, HardDrive, ShieldCheck } from 'lucide-react';
import { useMedia } from '@/contexts/MediaContext';
import { useAuth } from '@/contexts/AuthContext';
import MediaGrid from '@/components/MediaGrid';
import { motion } from 'framer-motion';
import { apiService } from '@/services/api';
import { USE_VPS_MEDIA } from '@/config/env';

const categoryLabels = {
  image: 'Imagens',
  video: 'Vídeos',
  audio: 'Áudios',
  gif: 'GIFs',
};

const formatBytes = (bytes = 0) => {
  const value = Number(bytes) || 0;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(value ? 1 : 0)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { files, loading, loadFiles, error } = useMedia();
  const [planUsage, setPlanUsage] = useState(null);

  useEffect(() => {
    if (user?.uid) {
      loadFiles(user.uid);
    }
  }, [user?.uid, loadFiles]);

  useEffect(() => {
    if (!USE_VPS_MEDIA || !user?.uid) return;

    const loadPlanUsage = async () => {
      try {
        const orgPayload = await apiService.getOrganizations();
        const organization = orgPayload?.organizations?.[0];
        if (!organization?.id) return;

        const usagePayload = await apiService.getOrganizationUsage(organization.id);
        setPlanUsage({
          ...usagePayload,
          organization
        });
      } catch (usageError) {
        console.warn('Não foi possível carregar a quota do plano:', usageError);
      }
    };

    loadPlanUsage();
  }, [user?.uid, files.length]);

  const recentMedia = useMemo(() => {
    if (!files || files.length === 0) return [];
    return [...files]
      .sort((a, b) => new Date(b.created_at || b.date || 0) - new Date(a.created_at || a.date || 0))
      .slice(0, 8);
  }, [files]);

  const categories = useMemo(() => {
    const defaultCats = {
      image: { id: 'image', name: 'Imagens', count: 0 },
      video: { id: 'video', name: 'Vídeos', count: 0 },
      audio: { id: 'audio', name: 'Áudios', count: 0 },
      gif: { id: 'gif', name: 'GIFs', count: 0 },
    };

    if (!files || files.length === 0) return Object.values(defaultCats);
    
    // Group files by type
    files.forEach((item) => {
      let type = 'outros';
      if (item.type) {
          if (item.type.includes('image')) type = 'image';
          else if (item.type.includes('video')) type = 'video';
          else if (item.type.includes('audio')) type = 'audio';
          else if (item.type.includes('gif')) type = 'gif';
      }
      
      if (defaultCats[type]) {
          defaultCats[type].count += 1;
      }
    });
    
    return Object.values(defaultCats);
  }, [files]);

  const isEmpty = (!loading && (files.length === 0));
  const displayRecent = recentMedia;
  const displayCategories = categories;

  const storageUsedBytes = Number(planUsage?.usage?.storageUsedBytes || 0);
  const storageLimitBytes = Number(planUsage?.subscription?.storage_limit_bytes || planUsage?.organization?.storage_limit_bytes || 0);
  const storagePercent = storageLimitBytes > 0
    ? Math.min((storageUsedBytes / storageLimitBytes) * 100, 100)
    : 0;
  const planCode = planUsage?.subscription?.plan_code || planUsage?.organization?.plan_code || 'free';
  const planName = planCode === 'master' ? 'Master' : planCode === 'professional' ? 'Profissional' : 'Gratuito';

  return (
    <div className="space-y-8 pb-10">
      {/* Hero Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-400 p-8 md:p-12 shadow-2xl shadow-cyan-500/20"
      >
        <div className="relative z-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white text-xs font-bold mb-4 uppercase tracking-wider">
            <Sparkles className="w-3 h-3" />
            <span>Bem-vindo ao MediaHub</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight leading-tight">
            Olá, {user?.displayName || user?.name || 'Usuário'}.
          </h1>
          <p className="text-blue-50 text-lg mb-8 max-w-xl leading-relaxed">
            Sua biblioteca, seus conteúdos e suas ferramentas de criação em um só lugar.
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => navigate('/media/image')}
              className="bg-white text-blue-600 hover:bg-blue-50 px-6 py-3 rounded-full font-bold shadow-lg transition-all transform hover:scale-105 active:scale-95 flex items-center gap-2"
            >
              Explorar mídia
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/profile')}
              className="bg-slate-950/20 text-white hover:bg-slate-950/30 px-6 py-3 rounded-full font-bold border border-white/25 transition-all"
            >
              Meu perfil
            </button>
          </div>
        </div>
        
        {/* Decorative Circles */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-20 -mb-20 w-64 h-64 bg-blue-900/20 rounded-full blur-2xl"></div>
      </motion.div>

      {USE_VPS_MEDIA && (
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-2xl p-5 md:p-6"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-cyan-500/10 text-cyan-400 flex items-center justify-center shrink-0">
                <HardDrive className="w-5 h-5" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-bold text-slate-100">Plano {planName}</h2>
                  <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    Beta Brasil 2026
                  </span>
                </div>
                <p className="text-sm text-slate-400 mt-1">
                  Testes gratuitos de outubro a dezembro de 2026 · ambiente restrito e protegido.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span>{formatBytes(storageUsedBytes)} de {storageLimitBytes ? formatBytes(storageLimitBytes) : '500 MB'} utilizados</span>
            </div>
          </div>

          <div className="mt-5 h-2.5 rounded-full bg-slate-800 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-500"
              style={{ width: `${storagePercent}%` }}
            />
          </div>
          <div className="mt-2 flex justify-between text-xs text-slate-500">
            <span>{storagePercent.toFixed(1)}% utilizado</span>
            <span>Grátis: 500 MB · Profissional: 20 GB · Master: 100 GB</span>
          </div>
        </motion.div>
      )}

      {/* Stats/Categories */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {displayCategories.map((cat, index) => (
          <motion.div
            key={cat.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.05 }}
            className="bg-slate-900/50 backdrop-blur-md border border-slate-800 p-5 rounded-2xl hover:bg-slate-800/50 transition-colors cursor-pointer group"
            onClick={() => navigate(`/media/${cat.id}`)}
          >
            <div className="flex justify-between items-start mb-3">
              <span className="text-slate-400 text-sm font-medium group-hover:text-cyan-400 transition-colors">{cat.name}</span>
              <TrendingUp className="w-4 h-4 text-slate-600 group-hover:text-cyan-400" />
            </div>
            <div className="text-3xl font-bold text-slate-100 group-hover:scale-110 origin-left transition-transform">
              {cat.count || 0}
            </div>
            <div className="text-xs text-slate-500 mt-1">arquivos armazenados</div>
          </motion.div>
        ))}
      </div>

      {/* Recent Files */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
            <span className="w-2 h-8 bg-cyan-500 rounded-full"></span>
            Recentes
          </h2>
          <button className="text-cyan-400 hover:text-cyan-300 text-sm font-medium flex items-center gap-1 transition-colors">
            Ver tudo <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-cyan-500" />
          </div>
        ) : isEmpty ? (
          <div className="bg-slate-900/40 border border-slate-800 rounded-2xl px-6 py-12 text-center">
            <h3 className="text-lg font-semibold text-slate-200">Sua biblioteca está vazia</h3>
            <p className="text-sm text-slate-500 mt-2">
              Use o botão Upload para adicionar sua primeira imagem, vídeo, áudio ou GIF.
            </p>
          </div>
        ) : (
          <MediaGrid 
            items={displayRecent} 
            onItemClick={(item) => navigate(`/media/item/${item.id}`)}
          />
        )}
      </motion.div>
    </div>
  );
};

export default Dashboard;

